// src/components/Modals/CreatorMemberModal.jsx
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  Box,
  Flex,
  Text,
  Image,
  Tag,
  Button,
  Link,
  Select,
  HStack,
  useToast,
} from "@chakra-ui/react";
import instagram from "../../images/insta_logo.png";
import { useEffect, useState } from "react";
import avatar from "../../../src/images/Avatar.png";
import facebook from "../../images/facebook_logo.png";
import youtube from "../../images/youtube_logo.png";
import tiktok from "../../images/tictok_logo.png";
import { createChat, creatorDetail, updateUserStatus } from "../../api";
import { useNavigate } from "react-router-dom";
import AuditLogPreview from "../AuditLogPreview";

const PLATFORM_INFO = {
  instagram: { icon: instagram, label: "인스타그램" },
  youtube: { icon: youtube, label: "유튜브" },
  tiktok: { icon: tiktok, label: "틱톡" },
  facebook: { icon: facebook, label: "페이스북" },
};

const statusMeta = {
  ACTIVE: { label: "활성", color: "green" },
  INACTIVE: { label: "비활성", color: "gray" },
  BANNED: { label: "차단", color: "red" },
};

const creatorDisplayName = (data) =>
  data?.displayName || data?.nickname || data?.socialAccounts?.[0]?.username || "-";

export function CreatorMemberModal({ isOpen, onClose, accountId, datas, onSuccess }) {
  const [data, setData] = useState(null);
  const navigate = useNavigate();
  const toast = useToast();

  const handleClickDM = async () => {
    try {
      const body = {
        targetProfileId: data.profileId,
        targetRole: "creator",
      };

      await createChat({ body });

      onClose();
      navigate(`/dm`);
    } catch (error) {
      console.error("채팅방 생성 실패:", error);
      alert("채팅방 생성에 실패했습니다. 다시 시도해주세요.");
    }
  };

  useEffect(() => {
    if (!accountId) return;
    loadDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId]);

  const loadDetail = async () => {
    try {
      const res = await creatorDetail(accountId);
      setData(res.data?.data);
    } catch (e) {
      console.error("상세 조회 실패", e);
    }
  };

  const handleStatusChange = async (status) => {
    if (!data?.userId || !status || data.userStatus === status) return;
    const ok = window.confirm(
      `${data?.displayName ?? data?.nickname ?? "계정"} 상태를 ${
        statusMeta[status]?.label ?? status
      }(으)로 변경할까요?`
    );
    if (!ok) return;

    try {
      await updateUserStatus({ userId: data.userId, status });
      toast({ status: "success", title: "계정 상태가 변경되었습니다." });
      await loadDetail();
      onSuccess?.();
    } catch (e) {
      toast({
        status: "error",
        title: "계정 상태 변경 실패",
        description: e?.response?.data?.message ?? "잠시 후 다시 시도해주세요.",
      });
    }
  };

  const primaryAccount =
    data?.socialAccounts?.find((acc) => acc.primary) ||
    data?.socialAccounts?.[0];

  const primaryIcon = primaryAccount
    ? PLATFORM_INFO[primaryAccount.platform]?.icon
    : instagram;

  const birth = data?.additionalInfo?.birthDate ?? "";
  const [year, month, day] = birth.split("-");

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" isCentered>
      <ModalOverlay />
      <ModalContent borderRadius="20px" p="10px" height="800px">
        <ModalHeader fontSize="22px">멤버 관리</ModalHeader>
        <ModalCloseButton />
        <ModalBody
          overflowY="auto"
          pb="30px"
          css={{
            "&::-webkit-scrollbar": {
              display: "none",
            },
            msOverflowStyle: "none", // IE, Edge
            scrollbarWidth: "none", // Firefox
          }}
        >
          <Box display="flex" flexDir="column">
            <Flex
              alignItems="stretch"
              flexDir="column"
              gap="12px"
              mb="20px"
              w="100%"
            >
              <Flex align="center" gap="12px" minW={0}>
                <Image src={avatar} w="40px" h="40px" />
                <Text
                  fontWeight="700"
                  fontSize="20px"
                  noOfLines={1}
                  minW={0}
                >
                  {creatorDisplayName(data)}
                </Text>
              </Flex>

              <Flex
                align="center"
                justify="space-between"
                gap="10px"
                w="100%"
                flexWrap="wrap"
              >
                <HStack spacing="8px" flexWrap="wrap">
                <Image src={primaryIcon} w="24px" h="24px" />
                <Tag
                  bg="#9050FF1A"
                  color="#9050FF"
                  fontWeight="700"
                  borderRadius="full"
                  px="10px"
                  py="4px"
                >
                  크리에이터
                </Tag>
                <HStack spacing="6px" onClick={(e) => e.stopPropagation()}>
                  <Tag colorScheme={statusMeta[data?.userStatus]?.color ?? "gray"}>
                    {statusMeta[data?.userStatus]?.label ?? data?.userStatus ?? "-"}
                  </Tag>
                  <Select
                    size="xs"
                    w="82px"
                    value={data?.userStatus ?? "ACTIVE"}
                    onChange={(e) => handleStatusChange(e.target.value)}
                    isDisabled={!data?.userId}
                  >
                    <option value="ACTIVE">활성</option>
                    <option value="INACTIVE">비활성</option>
                    <option value="BANNED">차단</option>
                  </Select>
                </HStack>
                </HStack>
                <Button
                  w="80px"
                  fontSize="16px"
                  fontWeight={500}
                  bg="#0054C0"
                  color="white"
                  border="1px solid #D1D4D9"
                  borderRadius="4px"
                  p="4px"
                  onClick={handleClickDM}
                >
                  DM
                </Button>
              </Flex>
            </Flex>
            <Flex flexDir="row" gap="10px">
              <Flex w="100%" flexDir="column">
                <Text fontSize="14px" fontWeight="600" mb="6px">
                  이름{" "}
                </Text>
                <Flex
                  bgColor="#F4F7FA"
                  mb="20px"
                  h="46px"
                  justify="center"
                  align="center"
                  borderRadius="8px"
                >
                  {creatorDisplayName(data)}
                </Flex>
              </Flex>
              <Flex w="100%" flexDir="column">
                <Text fontSize="14px" fontWeight="600" mb="6px">
                  이메일
                </Text>
                <Flex
                  bgColor="#F4F7FA"
                  mb="20px"
                  h="46px"
                  justify="center"
                  align="center"
                  borderRadius="8px"
                >
                  {data?.additionalInfo.email ?? ""}
                </Flex>
              </Flex>
            </Flex>
            <Flex flexDir="column" gap="8px" mb="20px">
              <Text fontSize="14px" fontWeight="600" mb="6px">
                SNS 채널 정보{" "}
                <Text as="span" fontSize="12px" color="#A2A9B3">
                  (보유한 채널만 등록)
                </Text>
              </Text>
            </Flex>
            <Flex flexDir="column" gap="20px">
              {Object.keys(PLATFORM_INFO).map((platform) => {
                const { icon } = PLATFORM_INFO[platform];
                const account = data?.socialAccounts?.find(
                  (acc) => acc.platform === platform
                );

                const hasData = !!account?.channelUrl; // 연동된 플랫폼 여부

                return (
                  <Flex key={platform} flexDir="column" gap="10px">
                    {/* 플랫폼 + 링크 또는 미연동 버튼 */}
                    <Flex align="center" gap="10px">
                      <Image src={icon} w="26px" />
                      {hasData ? (
                        <Link
                          href={account.channelUrl}
                          target="_blank"
                          fontSize="14px"
                          fontWeight={500}
                          color="#2864FF"
                          w="100%"
                          whiteSpace="nowrap"
                          overflow="hidden"
                          textOverflow="ellipsis"
                        >
                          {account.channelUrl}
                        </Link>
                      ) : (
                        <Button
                          bg="#E8EAEC"
                          color="#5D6979"
                          fontSize="13px"
                          fontWeight={600}
                          h="30px"
                          borderRadius="6px"
                          px="12px"
                        >
                          미연동
                        </Button>
                      )}
                    </Flex>

                    {/* 연동 플랫폼만 통계 박스 보여줌 */}
                    {hasData && (
                      <Flex gap="10px">
                        {["followers", "posts", "avgViews"].map((key) => {
                          const label =
                            key === "followers"
                              ? "팔로워"
                              : key === "posts"
                              ? "게시물"
                              : "평균 조회수";

                          return (
                            <Box
                              key={key}
                              flex="1"
                              border="1px solid #E8EAEC"
                              borderRadius="10px"
                              p="10px"
                              textAlign="center"
                            >
                              <Text fontSize="12px" color="#5D6979">
                                {label}
                              </Text>
                              <Text
                                fontSize="16px"
                                fontWeight={700}
                                color="#172940"
                              >
                                {Number(account[key])?.toLocaleString() ?? "-"}
                              </Text>
                            </Box>
                          );
                        })}
                      </Flex>
                    )}
                  </Flex>
                );
              })}
            </Flex>

            <Text fontSize="14px" fontWeight="600" my="6px">
              생년월일{" "}
            </Text>
            <Flex w="100%" flexDir="row" gap="8px">
              <Flex
                w="100%"
                bgColor="#F4F7FA"
                mb="20px"
                h="46px"
                justify="center"
                align="center"
                borderRadius="8px"
              >
                {year ?? ""}
              </Flex>
              <Flex
                w="100%"
                bgColor="#F4F7FA"
                mb="20px"
                h="46px"
                justify="center"
                align="center"
                borderRadius="8px"
              >
                {month ?? ""}
              </Flex>
              <Flex
                w="100%"
                bgColor="#F4F7FA"
                mb="20px"
                h="46px"
                justify="center"
                align="center"
                borderRadius="8px"
              >
                {day ?? ""}
              </Flex>
            </Flex>
            <Flex flexDir="column" mb="20px">
              <Text fontSize="14px" fontWeight="600" mb="6px">
                산업 카테고리
              </Text>
              <Box
                width="100%"
                overflowX="auto"
                overflowY="hidden"
                whiteSpace="nowrap"
                css={{
                  "&::-webkit-scrollbar": { display: "none" },
                }}
              >
                <Flex
                  gap="6px"
                  border="1px solid #D1D4D9"
                  p="12px"
                  borderRadius="6px"
                  width="100%" // 변경
                  minW="fit-content" // 내용이 많을 땐 스크롤 유지
                  flexWrap="nowrap"
                >
                  {data?.additionalInfo?.industryCategories?.map((category) => (
                    <Box
                      key={category.id}
                      px="8px"
                      py="2px"
                      bg="#E9F2FF"
                      color="#4B5563"
                      fontSize="12px"
                      fontWeight={500}
                      borderRadius="6px"
                      flexShrink={0}
                    >
                      {category.nameKo ?? ""}
                    </Box>
                  ))}
                </Flex>
              </Box>
            </Flex>
            <Flex flexDir="column" mb="20px">
              <Text fontSize="14px" fontWeight="600" mb="6px">
                라이프스타일 카테고리
              </Text>
              <Box
                width="100%" // 부모 폭 고정
                overflowX="auto"
                overflowY="hidden"
                whiteSpace="nowrap"
                css={{
                  "&::-webkit-scrollbar": { display: "none" },
                }}
              >
                <Flex
                  gap="6px"
                  border="1px solid #D1D4D9"
                  p="12px"
                  borderRadius="6px"
                  width="100%" // 변경
                  minW="fit-content" // 내용이 많을 땐 스크롤 유지
                  flexWrap="nowrap"
                >
                  {data?.additionalInfo?.lifestyleCategories?.map(
                    (category) => (
                      <Box
                        key={category.id}
                        px="8px"
                        py="2px"
                        bg="#E9F2FF"
                        color="#4B5563"
                        fontSize="12px"
                        fontWeight={500}
                        borderRadius="6px"
                        flexShrink={0}
                      >
                        {category.nameKo ?? ""}
                      </Box>
                    )
                  )}
                </Flex>
              </Box>
            </Flex>
            <Flex flexDir="row" gap="10px">
              <Flex w="100%" flexDir="column">
                <Text fontSize="14px" fontWeight="600" mb="6px">
                  성별{" "}
                </Text>
                <Flex
                  bgColor="#F4F7FA"
                  mb="20px"
                  h="46px"
                  justify="center"
                  align="center"
                  borderRadius="8px"
                >
                  {data?.additionalInfo?.gender === "male"
                    ? "남성"
                    : data?.additionalInfo?.gender === "female"
                    ? "여성"
                    : "기타"}
                </Flex>
              </Flex>
              <Flex w="100%" flexDir="column">
                <Text fontSize="14px" fontWeight="600" mb="6px">
                  국가{" "}
                </Text>
                <Flex
                  bgColor="#F4F7FA"
                  mb="20px"
                  h="46px"
                  justify="center"
                  align="center"
                  borderRadius="8px"
                >
                  {data?.additionalInfo?.country ?? ""}
                </Flex>
              </Flex>
              <Flex w="100%" flexDir="column">
                <Text fontSize="14px" fontWeight="600" mb="6px">
                  언어{" "}
                </Text>
                <Flex
                  bgColor="#F4F7FA"
                  mb="20px"
                  h="46px"
                  justify="center"
                  align="center"
                  borderRadius="8px"
                >
                  {data?.additionalInfo.language ?? ""}
                </Flex>
              </Flex>
            </Flex>
            <AuditLogPreview targetType="USER" targetId={data?.userId} />
          </Box>
          <Flex justifyContent="center" mt="20px">
            <Button
              w="104px"
              h="44px"
              fontWeight={700}
              fontSize="14px"
              bgColor="#0054C0"
              color="white"
              borderRadius="12px"
              onClick={onClose}
            >
              확인
            </Button>
          </Flex>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
